import {FC, useState} from 'react';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {Box, Button, IconButton, Tooltip, Typography} from '@mui/material';
import {useSnackbar} from 'notistack';

import {Event, EventParticipant} from '@entities/event';
import {
  Participant,
  ParticipantSelect,
  useGetParticipant,
} from '@entities/participant';
import {
  ROUTE__PARTICIPANT_CREATE,
  ROUTE__PARTICIPANT_DETAILS,
} from '@shared/constants';
import {Header} from '@widgets/common';

interface Props {
  event: Event;
}

const LogoPreview: FC<{src: string; alt?: string; label: string}> = ({
  src,
  alt,
  label,
}) => (
  <Box>
    <Typography color="text.secondary" sx={{fontSize: 12, mb: 0.5}}>
      {label}
    </Typography>
    <Box
      width={80}
      height={80}
      border="1px solid #E0E0E0"
      borderRadius={1}
      bgcolor="#fafafa"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{width: '100%', height: '100%', objectFit: 'contain'}}
      />
    </Box>
  </Box>
);

// EventParticipant (from GET /events/{id}) carries only a subset of
// Participant's fields. Wrap it into a minimal Participant so
// ParticipantSelect can render it as a value.
const toParticipant = (
  p: EventParticipant | null | undefined,
): Participant | null =>
  p
    ? {
        participantId: p.id,
        names: [p.name],
        defaultName: p.name,
        localizedName: {} as Participant['localizedName'],
        categorySlug: '',
        subcategorySlug: null,
        colors: null,
        abbreviation: null,
        externalParticipantIds: null,
        logoUrl: p.logoUrl,
        logoUrlStroke: p.logoUrlStroke,
        logoUrlPlayer: p.logoUrlPlayer,
        logoUrlCustom: p.logoUrlCustom,
        nation: p.nation,
      }
    : null;

export const EventParticipants: FC<Props> = ({event}) => {
  const {enqueueSnackbar} = useSnackbar();

  // Two participants (Yes/No) when the event is set up that way, otherwise one.
  const isBinary = Boolean(
    event.yesParticipant ||
      event.noParticipant ||
      event.yesParticipantId ||
      event.noParticipantId,
  );

  const [participant, setParticipant] = useState<Participant | null>(() =>
    toParticipant(event.participant),
  );
  const [yesParticipant, setYesParticipant] = useState<Participant | null>(() =>
    toParticipant(event.yesParticipant),
  );
  const [noParticipant, setNoParticipant] = useState<Participant | null>(() =>
    toParticipant(event.noParticipant),
  );
  const {data: participantDetails} = useGetParticipant(
    participant?.participantId ?? '',
    {enabled: !!participant?.participantId},
  );
  const {data: yesParticipantDetails} = useGetParticipant(
    yesParticipant?.participantId ?? '',
    {enabled: !!yesParticipant?.participantId},
  );
  const {data: noParticipantDetails} = useGetParticipant(
    noParticipant?.participantId ?? '',
    {enabled: !!noParticipant?.participantId},
  );

  const categorySlug = event.category?.id;

  const fields = isBinary
    ? [
        {
          label: 'Yes participant',
          value: yesParticipantDetails ?? yesParticipant,
          onChange: setYesParticipant,
        },
        {
          label: 'No participant',
          value: noParticipantDetails ?? noParticipant,
          onChange: setNoParticipant,
        },
      ]
    : [
        {
          label: 'Participant',
          value: participantDetails ?? participant,
          onChange: setParticipant,
        },
      ];

  const handleSave = () => {
    // TODO: replace with the real save endpoint once it is available.
    enqueueSnackbar('Saving participants is not implemented yet', {
      variant: 'info',
    });
  };

  return (
    <>
      <Header
        title="Participants"
        mb={5}
        isBackButtonShown={false}
        leftPart={
          <Tooltip title="Create participant in new tab">
            <IconButton
              size="small"
              component="a"
              href={ROUTE__PARTICIPANT_CREATE}
              target="_blank"
              rel="noopener noreferrer"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <Box display="flex" gap={3}>
        {fields.map(({label, value, onChange}) => (
          <Box key={label} flex={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Box flex={1}>
                <ParticipantSelect
                  label={label}
                  value={value}
                  onChange={onChange}
                  categorySlug={categorySlug}
                />
              </Box>
              <Tooltip
                title={
                  value
                    ? 'Open participant in new tab'
                    : 'Select a participant first'
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!value}
                    component={value ? 'a' : 'button'}
                    href={
                      value
                        ? ROUTE__PARTICIPANT_DETAILS.replace(
                            ':participant_id',
                            value.participantId,
                          )
                        : undefined
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {value && (value.logoUrl || value.logoUrlCustom) && (
              <Box mt={1.5} display="flex" gap={1.5}>
                {value.logoUrl && (
                  <LogoPreview
                    src={value.logoUrl}
                    alt={value.names[0]}
                    label="Logo"
                  />
                )}
                {value.logoUrlCustom && (
                  <LogoPreview
                    src={value.logoUrlCustom}
                    alt={value.names[0]}
                    label="Custom logo"
                  />
                )}
              </Box>
            )}
          </Box>
        ))}
      </Box>
      <Box mt={3}>
        <Button onClick={handleSave}>Save participants</Button>
      </Box>
    </>
  );
};
